import { useState, useMemo, useEffect } from 'react';
import {
  FaExternalLinkAlt, FaTimes, FaLaptopCode,
  FaMobileAlt, FaServer, FaTools
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { fadeIn, fadeInUp, staggerContainer } from '../utils/animations';
import { useTranslation } from 'react-i18next';
import messaging from '../assets/images/messaggera.png'
import dentyshare from '../assets/images/dentyshare.png'
import wequizz from '../assets/images/wequizz.png'
import sultan from '../assets/images/sultan.jpeg';
//@ts-ignore
import ostedhy from '../assets/images/ostedhy.webp'
import mernStarter from '../assets/images/mernstarter.png'
import linkFoot from '../assets/images/linkfoot.png'

interface Project {
    id: string;
    title: string;
    description: string;
    longDescription?: string;
    imageUrl: string;
    additionalImages?: string[];
    githubLink?: string;
    liveLink: string;
    technologies: string[];
    category: 'web' | 'mobile' | 'backend' | 'other';
    featured: boolean;
    completionDate: string;
    keyFeatures?: string[];
    challenges?: string[];
    role?: string;
}



const Projects: React.FC = () => {
    const { t } = useTranslation();
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [activeCategory, setActiveCategory] = useState<string>('all');
    const [activeTab, setActiveTab] = useState<number>(0);
    const { ref, isInView } = useInView(0.1);

    const projects = useMemo((): Project[] => [
        {
            id: "ostedhy",
            title: "Ostedhy",
            description: t('projects.descriptions.ostedhy'),
            longDescription: t('projects.longDescriptions.ostedhy'),
            imageUrl: ostedhy, 
            liveLink: "https://ostedhy.com",
            technologies: ["MySQL", "Sequelize", "Redis", "Express", "AWS", "Socket.io", "Node.js"],
            category: 'web',
            featured: true,
            completionDate: "2023",
            keyFeatures: t('projects.keyFeatures.ostedhy', { returnObjects: true }) as string[],
            challenges: t('projects.challenges.ostedhy', { returnObjects: true }) as string[]
        },
        {
            id: "messaggera",
            title: "Messaggera",
            description: t('projects.descriptions.messaggera'),
            longDescription: t('projects.longDescriptions.messaggera'),
            imageUrl: messaging,
            githubLink: "https://github.com/yourusername/ecommerce-platform",
            liveLink: "https://messaggera.com",
            technologies: ["React", "Node.js", "Express", "MongoDB", "Socket.io", "Redis", "Chart.js"],
            category: 'web',
            featured: true,
            completionDate: "2023",
            keyFeatures: t('projects.keyFeatures.messaggera', { returnObjects: true }) as string[],
            challenges: t('projects.challenges.messaggera', { returnObjects: true }) as string[],
            role: "Lead Developer"
        },
        {
            id: "wequizz",
            title: "WeQuizz",
            description: t('projects.descriptions.wequizz'),
            longDescription: t('projects.longDescriptions.wequizz'),
            imageUrl: wequizz,
            githubLink: "https://github.com/yourusername/task-management-app",
            liveLink: "https://wequizz.com",
            technologies: ["React", "Node.js", "Express", "MongoDB", "Material-UI", "Redux", "Socket.io", "Chart.js"],
            category: 'web',
            featured: true,
            completionDate: "2022",
            keyFeatures: t('projects.keyFeatures.wequizz', { returnObjects: true }) as string[],
            challenges: t('projects.challenges.wequizz', { returnObjects: true }) as string[],
            role: "Full Stack Developer"
        },
        {
            id: "dentyshare",
            title: "Dentyshare",
            description: t('projects.descriptions.dentyshare'),
            longDescription: t('projects.longDescriptions.dentyshare'),
            imageUrl: dentyshare,
            githubLink: "https://github.com/yourusername/weather-forecast-dashboard",
            liveLink: "https://stagingdentyshare.softylines.com",
            technologies: ["React", "Node.js", "Express", "MongoDB", "Material-UI", "Redux", "AWS S3"],
            category: 'web',
            featured: false,
            completionDate: "2022",
            keyFeatures: t('projects.keyFeatures.dentyshare', { returnObjects: true }) as string[],
            challenges: t('projects.challenges.dentyshare', { returnObjects: true }) as string[],
            role: "Frontend Developer"
        },
        {
            id: "sultan",
            title: "Sultan",
            description: t('projects.descriptions.sultan'),
            longDescription: t('projects.longDescriptions.sultan'),
            imageUrl: sultan,
            githubLink: "https://github.com/yourusername/weather-forecast-dashboard",
            liveLink: "https://apps.apple.com/tn/app/sultan/id6738101247",
            technologies: ["React Native", "Node.js", "Express", "MongoDB", "Socket.io", "Redux", "Firebase"],
            category: 'mobile',
            featured: true,
            completionDate: "2023",
            keyFeatures: t('projects.keyFeatures.sultan', { returnObjects: true }) as string[],
            challenges: t('projects.challenges.sultan', { returnObjects: true }) as string[],
            role: "Mobile Developer"
        },
        {
            id: "mernStarter",
            title: "MERN Starter",
            description: t('projects.descriptions.mernStarter'),
            longDescription: t('projects.longDescriptions.mernStarter'),
            imageUrl: mernStarter,
            liveLink: "https://mernstarter.tech/",
            technologies: ["React", "Node.js", "Express", "MongoDB", "JWT", "TypeScript", "Tailwind CSS"],
            category: 'web',
            featured: true,
            completionDate: "2025",
            keyFeatures: t('projects.keyFeatures.mernStarter', { returnObjects: true }) as string[],
            challenges: t('projects.challenges.mernStarter', { returnObjects: true }) as string[],
            role: "Full Stack Developer"
        },
        {
            id: "linkFoot",
            title: "LinkeFoot",
            description: t('projects.descriptions.linkFoot'),
            longDescription: t('projects.longDescriptions.linkFoot'),
            imageUrl: linkFoot,
            liveLink: "https://linkefoot.com",
            technologies: ["Node.js", "Express", "MongoDB", "Socket.io", "JWT", "Redis", "AWS"],
            category: 'web',
            featured: true,
            completionDate: "2023",
            keyFeatures: t('projects.keyFeatures.linkFoot', { returnObjects: true }) as string[],
            challenges: t('projects.challenges.linkFoot', { returnObjects: true }) as string[],
            role: "Backend Developer"
        }
    ], [t]);

    const categories = useMemo(() => [
        { id: 'all', label: t('projects.categories.all'), icon: <FaLaptopCode /> },
        { id: 'web', label: t('projects.categories.web'), icon: <FaLaptopCode /> },
        { id: 'mobile', label: t('projects.categories.mobile'), icon: <FaMobileAlt /> },
    ], [t]);

    const projectsByCategory = useMemo(() => {
        const result: Record<string, Project[]> = {
            all: projects
        };

        categories.forEach(category => {
            if (category.id !== 'all') {
                result[category.id] = projects.filter(project => project.category === category.id);
            }
        });

        return result;
    }, [categories, projects]);

    const openProjectDetails = (project: Project) => {
        setSelectedProject(project);
        document.body.style.overflow = 'hidden';
    };

    const closeProjectDetails = () => {
        setSelectedProject(null);
        document.body.style.overflow = 'auto';
    };

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && selectedProject) {
                closeProjectDetails();
            }
        };

        if (selectedProject) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [selectedProject]);

    const getProjectCategoryIcon = (category: string) => {
        switch(category) {
            case 'web': return <FaLaptopCode className="text-primary-400" />;
            case 'mobile': return <FaMobileAlt className="text-accent-400" />;
            case 'backend': return <FaServer className="text-purple-400" />;
            default: return <FaTools className="text-yellow-400" />;
        }
    };

    return (
        <motion.section
            id="projects"
            className="py-24 relative"
            ref={ref as any}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeIn}
        >
            <div className="absolute top-1/3 right-0 w-1/3 h-1/3 bg-primary-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5 hidden md:block"></div>
            <div className="absolute bottom-1/3 left-0 w-1/3 h-1/3 bg-accent-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5 hidden md:block"></div>

            <div className="responsive-container">
                <motion.div 
                    className="text-center mb-16"
                    variants={fadeInUp}
                >
                    <motion.h2
                        className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
                        variants={fadeInUp}
                    >
                        {t('projects.title')}
                    </motion.h2>
                    <motion.p
                        className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed"
                        variants={fadeInUp}
                    >
                        {t('projects.subtitle')}
                    </motion.p>
                </motion.div>

                <motion.div 
                    className="mb-16"
                    variants={fadeInUp}
                >
                    <div className="flex flex-wrap justify-center gap-3">
                        {categories.map((category, index) => (
                            <motion.button
                                key={category.id}
                                className={`px-6 py-3 rounded-full flex items-center gap-3 text-sm font-medium transition-all duration-300
                                  ${
                                    activeCategory === category.id
                                        ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-500/25'
                                        : 'bg-gray-800/50 backdrop-blur-sm text-gray-300 hover:text-white hover:bg-gray-700/50'
                                  }`}
                                onClick={() => setActiveCategory(category.id)}
                            >
                                <div className={`text-lg transition-colors duration-300 ${activeCategory === category.id ? 'text-white' : 'text-gray-400'}`}>
                                    {category.icon}
                                </div>
                                <span>{category.label}</span>
                            </motion.button>
                        ))}
                    </div>
                </motion.div>

                <motion.div 
                    className="relative" 
                    style={{ height: 'auto', minHeight: '300px' }}
                    variants={staggerContainer}
                >
                    {Object.entries(projectsByCategory).map(([category, categoryProjects]) => (
                        <div
                            key={category}
                            className={`w-full relative transition-all duration-500 ease-out`}
                            style={{
                                position: 'relative',
                                opacity: activeCategory === category ? 1 : 0,
                                transform: activeCategory === category ? 'translateY(0)' : 'translateY(20px)',
                                pointerEvents: activeCategory === category ? 'auto' : 'none',
                                height: activeCategory === category ? 'auto' : '0',
                                overflow: activeCategory === category ? 'visible' : 'hidden',
                                zIndex: activeCategory === category ? 1 : 0,
                                willChange: 'opacity, transform'
                            }}
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-8">
                                {categoryProjects.map((project, index) => (
                                <motion.div
                                    key={project.id}
                                    className={`group relative bg-gray-900 rounded-xl overflow-hidden border transition-all duration-300 cursor-pointer ${
                                        project.id === "mernStarter" 
                                            ? "border-primary-500/50 hover:border-primary-400/70 hover:shadow-glow-md" 
                                            : "border-gray-700/30 hover:border-primary-500/50"
                                    }`}
                                    onClick={() => openProjectDetails(project)}
                                    animate={project.id === "mernStarter" ? {
                                        y: [0, -8, 0],
                                        scale: [1, 1.02, 1],
                                    } : {}}
                                    transition={project.id === "mernStarter" ? {
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    } : {}}
                                >
                                    <div className="relative h-52 overflow-hidden">
                                        <img
                                            src={project.imageUrl}
                                            alt={project.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            loading="lazy"
                                            width="400"
                                            height="225"
                                            decoding="async"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent"></div>
                                        
                                        <div className="absolute top-3 left-3">
                                            <div className="flex items-center gap-1.5 px-2 py-1 bg-black/70 backdrop-blur-sm rounded-full border border-gray-600/30">
                                                {getProjectCategoryIcon(project.category)}
                                                <span className="text-xs text-gray-200 font-medium">{project.category}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="absolute top-3 right-3">
                                            <div className={`px-2 py-1 backdrop-blur-sm rounded-full text-xs font-medium border ${
                                                project.id === "mernStarter" 
                                                    ? "bg-gradient-to-r from-primary-500/80 to-accent-500/80 text-white border-primary-400/50 animate-pulse" 
                                                    : "bg-black/70 text-gray-200 border-gray-600/30"
                                            }`}>
                                                {project.completionDate}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-5">
                                        <h3 className="text-xl font-bold mb-3 text-white group-hover:text-primary-300 transition-colors duration-300">
                                            {project.title}
                                        </h3>

                                        <p className="text-gray-400 mb-4 text-sm leading-relaxed line-clamp-3">
                                            {project.description}
                                        </p>

                                        <div className="flex flex-wrap gap-2 mb-5">
                                            {project.technologies.slice(0, 4).map((tech, techIndex) => (
                                                <span
                                                    key={techIndex}
                                                    className="px-3 py-1.5 bg-gray-800/60 backdrop-blur-sm text-gray-300 rounded-lg text-xs font-medium border border-gray-700/30
                                                      transition-all duration-300 group-hover:bg-primary-500/20 group-hover:text-primary-300 group-hover:border-primary-500/30"
                                                >
                                                    {tech}
                                                </span>
                                            ))}
                                            {project.technologies.length > 4 && (
                                                <span className="px-3 py-1.5 bg-gray-800/60 backdrop-blur-sm text-gray-400 rounded-lg text-xs border border-gray-700/30">
                                                    +{project.technologies.length - 4}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <button className="text-primary-400 text-sm font-semibold group-hover:text-primary-300 transition-colors duration-300 flex items-center gap-1">
                                                <span>{t('projects.viewDetails')}</span>
                                                <span className="transform group-hover:translate-x-0.5 transition-transform duration-300">→</span>
                                            </button>

                                            <a
                                                href={project.liveLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-1.5 rounded-full bg-gray-800/50 text-gray-400 hover:text-white hover:bg-primary-500/20 transition-all duration-300"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <FaExternalLinkAlt size={14} />
                                            </a>
                                        </div>
                                    </div>
                                </motion.div>
                                ))}
                            </div>
                        </div>
                    ))}
                </motion.div>

                {activeCategory !== 'all' && projectsByCategory[activeCategory]?.length === 0 && (
                    <motion.div
                        className="text-center py-12 bg-gray-900 rounded-lg"
                        variants={fadeInUp}
                    >
                        <p className="text-gray-300 text-lg">No projects found in this category.</p>
                    </motion.div>
                )}
            </div>

            {selectedProject && (
                <div
                    className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={closeProjectDetails}
                >
                    <div
                        className="bg-gray-900 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="relative h-64 md:h-80">
                            <img
                                src={selectedProject.imageUrl}
                                alt={selectedProject.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                width="800"
                                height="400"
                                decoding="async"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
                            
                            <button
                                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/80 transition-all duration-300"
                                onClick={closeProjectDetails}
                            >
                                <FaTimes size={18} />
                            </button>
                            
                            <div className="absolute bottom-6 left-6 right-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-primary-500/20 backdrop-blur-sm flex items-center justify-center border border-primary-500/30">
                                        {getProjectCategoryIcon(selectedProject.category)}
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-300 font-medium">{selectedProject.category.toUpperCase()} • {selectedProject.completionDate}</div>
                                        <h3 className="text-3xl font-bold text-white">{selectedProject.title}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex bg-gray-800/30 border-b border-gray-700/50">
                            <button
                                className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                                    activeTab === 0 
                                        ? 'text-primary-400 border-b-2 border-primary-400 bg-primary-500/10' 
                                        : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                                }`}
                                onClick={() => setActiveTab(0)}
                            >
                                {t('projects.modal.projectOverview')}
                            </button>
                            <button
                                className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                                    activeTab === 1 
                                        ? 'text-primary-400 border-b-2 border-primary-400 bg-primary-500/10' 
                                        : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                                }`}
                                onClick={() => setActiveTab(1)}
                            >
                                {t('projects.modal.keyFeatures')}
                            </button>
                            <button
                                className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                                    activeTab === 2 
                                        ? 'text-primary-400 border-b-2 border-primary-400 bg-primary-500/10' 
                                        : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                                }`}
                                onClick={() => setActiveTab(2)}
                            >
                                {t('projects.modal.technologies')}
                            </button>
                        </div>

                        <div className="p-8 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 320px)' }}>
                                {activeTab === 0 && (
                                    <div className="space-y-6">
                                        <div>
                                            <h4 className="text-xl font-bold mb-4 text-white">{t('projects.modal.projectOverview')}</h4>
                                            <p className="text-gray-300 leading-relaxed">
                                                {selectedProject.longDescription || selectedProject.description}
                                            </p>
                                        </div>

                                        <div className="bg-gray-800/30 rounded-lg p-4">
                                            <h4 className="text-lg font-semibold mb-3 text-white">{t('projects.modal.myRole')}</h4>
                                            <p className="text-gray-300 leading-relaxed">{selectedProject.role || 'MERN Stack Developer'}</p>
                                        </div>

                                        {selectedProject.challenges && (
                                            <div className="bg-gray-800/30 rounded-lg p-4">
                                                <h4 className="text-lg font-semibold mb-3 text-white">{t('projects.modal.keyChallenges')}</h4>
                                                <ul className="space-y-2">
                                                    {selectedProject.challenges.map((challenge, index) => (
                                                        <li key={index} className="text-gray-300 flex items-start">
                                                            <span className="text-primary-400 mr-2 mt-1">•</span>
                                                            <span>{challenge}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 1 && (
                                    <div>
                                        <h4 className="text-xl font-bold mb-4 text-white">{t('projects.modal.keyFeatures')}</h4>
                                        {selectedProject.keyFeatures ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {selectedProject.keyFeatures.map((feature, index) => (
                                                    <div key={index} className="bg-gray-800/30 rounded-lg p-3">
                                                        <div className="flex items-start">
                                                            <div className="w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center mr-2 mt-0.5">
                                                                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                                                            </div>
                                                            <div>
                                                                <p className="text-gray-300 text-sm leading-relaxed">{feature}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <p className="text-gray-400">No detailed features available for this project.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 2 && (
                                    <div>
                                        <h4 className="text-xl font-bold mb-4 text-white">{t('projects.modal.technologies')}</h4>
                                        
                                        <div className="mb-6">
                                            <div className="flex flex-wrap gap-2">
                                                {selectedProject.technologies.map((tech, index) => (
                                                    <div key={index} className="px-3 py-1.5 bg-gray-800/30 rounded-md text-gray-300 flex items-center gap-2 text-sm">
                                                        <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                                                        <span className="font-medium">{tech}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="bg-gray-800/30 rounded-lg p-4 text-center">
                                            <h5 className="text-base font-semibold mb-2 text-white">{t('projects.readyToExplore')}</h5>
                                            <p className="text-gray-400 mb-3 text-sm">{t('projects.checkOutDemo')}</p>
                                            <a
                                                href={selectedProject.liveLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-accent-500 rounded-md text-white font-semibold text-sm hover:shadow-lg transition-all duration-300"
                                            >
                                                <FaExternalLinkAlt size={14} /> {t('projects.viewLiveDemo')}
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </motion.section>
        );
    };

    export default Projects;
